// src/components/editions/BuildEditionButton.tsx
//
// **Build edition** (plan 2026-10-23-an-edition-is-an-argument-keyed-
// snapshot, C6): confirm what it does, start a preview edition, and hand the
// rest to the Editions drawer, which polls the new row until it settles. The
// server decides everything but the optional `period` (Decision 7): window,
// version and identity are its own.
//
// Refusals come back as a dict `detail.code` (C4): 409 an edition is already
// being built (the drawer is where it can be followed), 429 a daily cap
// (named), 422 a report too large for one edition (named), 503 not queued.
import { useState } from "react";
import { Button, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconStack2 } from "@tabler/icons-react";

import { buildEdition, editionBuildRefusal, type EditionStarted } from "@/api/editions";
import type { ItemPeriod } from "@/api/dashboards";
import { useConfirmDialog } from "@/components/modals/confirmDialog";
import { EDITION_COPY, refusalMessage } from "./editionCopy";

export type BuildEditionButtonProps = {
  dashboardId: string;
  /** A window for this preview only (phase D's "Build an edition for this
   *  window"); absent = the report's own default. */
  period?: ItemPeriod;
  /** The build started, or one was already running: show the editions. */
  onShowEditions: (started: EditionStarted | null) => void;
};

export default function BuildEditionButton({ dashboardId, period, onShowEditions }: BuildEditionButtonProps) {
  const confirm = useConfirmDialog();
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const started = await buildEdition(dashboardId, period);
      notifications.show({ color: "blue", message: EDITION_COPY.started });
      onShowEditions(started);
    } catch (err) {
      const refusal = editionBuildRefusal(err);
      if (refusal?.code === "edition_in_progress") {
        notifications.show({ color: "yellow", message: refusalMessage(refusal) });
        onShowEditions(null);
      } else {
        notifications.show({
          color: "red",
          title: EDITION_COPY.buildFailed,
          message: refusal ? refusalMessage(refusal) : EDITION_COPY.enqueueFailed,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="xs"
      variant="light"
      leftSection={<IconStack2 size={14} />}
      loading={busy}
      onClick={() =>
        confirm({
          title: EDITION_COPY.confirmTitle,
          children: <Text size="sm">{EDITION_COPY.confirmBody}</Text>,
          labels: { confirm: EDITION_COPY.confirmLabel, cancel: EDITION_COPY.cancel },
          confirmColor: "blue",
          onConfirm: start,
        })
      }
    >
      {EDITION_COPY.buildButton}
    </Button>
  );
}
